import React, { useState, useEffect, Fragment } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Dialog, Transition } from "@headlessui/react";
import api from "../api/api"; // Assuming your API utility is in this path
import { StatsCard } from "../components/StatsCard";
import { UserCard } from "../components/UserCard";
import Pagination from "../components/Pagination";

const Walker = () => {
    const [walkers, setWalkers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editWalker, setEditWalker] = useState(null);
    const [meta, setMeta] = useState({
        total: 0,
        per_page: 10,
        current_page: 1,
        last_page: 1,
    });

    // Form state for Create and Edit
    const [formData, setFormData] = useState({
        name: "",
        contact: "",
        location: "",
        status: "Active",
    });

    // Fetch Walkers on mount and pagination change
    useEffect(() => {
        fetchWalkers();
    }, [meta.current_page]);

    const fetchWalkers = async () => {
        setLoading(true);
        try {
            const response = await api.get("/users", {
                params: {
                    type: "walker",
                    page: meta.current_page,
                },
            });
            setWalkers(response.data);
            setMeta(response.meta);
            setLoading(false);
        } catch (error) {
            toast.error("Failed to fetch walkers.");
            setLoading(false);
        }
    };

    const handleAddNewWalker = () => {
        setEditWalker(null); // Reset form
        setFormData({
            name: "",
            contact: "",
            location: "",
            status: "Active",
        });
        setIsModalOpen(true);
    };

    // const handleEditWalker = (walker) => {
    //     setEditWalker(walker);
    //     setFormData({
    //         name: walker.name,
    //         contact: walker.contact,
    //         location: walker.location,
    //         status: walker.status,
    //     });
    //     setIsModalOpen(true);
    // };

    // const handleDeleteWalker = async (id) => {
    //     if (window.confirm("Are you sure you want to delete this walker?")) {
    //         try {
    //             await api.delete(`/users/${id}`);
    //             toast.success("Walker deleted successfully.");
    //             fetchWalkers(); // Refresh data
    //         } catch (error) {
    //             toast.error("Failed to delete walker.");
    //         }
    //     }
    // };

    const handleSaveWalker = async (e) => {
        e.preventDefault();
        try {
            if (editWalker) {
                // Update Walker
                await api.put(`/users/${editWalker.id}`, {
                    ...formData,
                    type: "walker",
                });
                toast.success("Walker updated successfully.");
            } else {
                // Create Walker
                await api.post("/users", {
                    ...formData,
                    type: "walker",
                });
                toast.success("Walker created successfully.");
            }
            setIsModalOpen(false); // Close modal
            fetchWalkers(); // Refresh list
        } catch (error) {
            toast.error("Error saving walker. Please check the form.");
        }
    };

    // Pagination Handlers
    const handleNextPage = () => {
        if (meta.current_page < meta.last_page) {
            setMeta((prev) => ({ ...prev, current_page: prev.current_page + 1 }));
        }
    };

    const handlePrevPage = () => {
        if (meta.current_page > 1) {
            setMeta((prev) => ({ ...prev, current_page: prev.current_page - 1 }));
        }
    };

    return (
        <div className="min-h-screen p-8">
            {/* Header Section */}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-[#3E358F] text-3xl font-bold">Walkers Overview</h1>
                <button
                    onClick={handleAddNewWalker}
                    className="bg-primary text-white px-4 py-2 rounded-lg flex items-center hover:bg-accent"
                >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Add New Walker
                </button>
            </div>

            {/* Stats Section */}
            <div className="grid grid-cols-3 gap-6 mb-12">
                <StatsCard title="Total Walker" value={meta.total} />
                <StatsCard title="Active Walker" value="5k" />{" "}
                {/* Adjust if data available */}
            </div>

            {/* Table Section */}
            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                {loading ? (
                    <div className="text-center py-6">Loading...</div>
                ) : (
                    <>
                        {walkers.map((walker) => (
                            <UserCard user={walker} />
                        ))}
                    </>
                )}
            </div>

            {/* Smart Pagination */}
            <Pagination
                meta={meta}
                onPageChange={(page) => setMeta(prev => ({ ...prev, current_page: page }))}
            />

            {/* Modal for Create/Edit */}
            {isModalOpen && (
                <Transition appear show={true} as={Fragment}>
                    <Dialog
                        as="div"
                        className="relative z-50"
                        onClose={() => setIsModalOpen(false)}
                    >
                        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
                        <div className="fixed inset-0 z-10 overflow-y-auto">
                            <div className="flex min-h-full items-center justify-center p-4 text-center">
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-medium leading-6 text-gray-900"
                                    >
                                        {editWalker ? "Edit Walker" : "Add New Walker"}
                                    </Dialog.Title>
                                    <form onSubmit={handleSaveWalker}>
                                        <div className="mt-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Name
                                            </label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, name: e.target.value })
                                                }
                                                required
                                                className="block w-full p-2 border rounded-lg"
                                            />
                                        </div>
                                        <div className="mt-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Contact
                                            </label>
                                            <input
                                                type="text"
                                                name="contact"
                                                value={formData.contact}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, contact: e.target.value })
                                                }
                                                required
                                                className="block w-full p-2 border rounded-lg"
                                            />
                                        </div>
                                        <div className="mt-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Location
                                            </label>
                                            <input
                                                type="text"
                                                name="location"
                                                value={formData.location}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, location: e.target.value })
                                                }
                                                required
                                                className="block w-full p-2 border rounded-lg"
                                            />
                                        </div>
                                        <div className="mt-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Status
                                            </label>
                                            <select
                                                name="status"
                                                value={formData.status}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, status: e.target.value })
                                                }
                                                className="block w-full p-2 border rounded-lg"
                                            >
                                                <option value="Active">Active</option>
                                                <option value="Inactive">Inactive</option>
                                            </select>
                                        </div>
                                        <div className="flex justify-end mt-6">
                                            <button
                                                type="button"
                                                onClick={() => setIsModalOpen(false)}
                                                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg mr-2"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="bg-primary text-white px-4 py-2 rounded-lg"
                                            >
                                                {editWalker ? "Update" : "Save"}
                                            </button>
                                        </div>
                                    </form>
                                </Dialog.Panel>
                            </div>
                        </div>
                    </Dialog>
                </Transition>
            )}
        </div>
    );
};

export default Walker;
